import { useState } from 'react'
import { supabase } from '../supabaseClient'

const TERMLY_TOS = "https://app.termly.io/policy-viewer/policy.html?policyUUID=4e17f386-81dc-4768-bcbe-07533afc4246"
const TERMLY_PRIVACY = "https://app.termly.io/policy-viewer/policy.html?policyUUID=db195f52-eb28-4320-aa6a-f18a74e7c41a"

export default function LoginScreen() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [loading, setLoading] = useState(false)
  const [mode, setMode] = useState("login") // "login" | "signup"
  const [signupSuccess, setSignupSuccess] = useState(false)
  const [tosAgreed, setTosAgreed] = useState(false)
  const [tosError, setTosError] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  async function handleSubmit() {
    setError("")
    setTosError(false)
    if (mode === "signup" && !tosAgreed) {
      setTosError(true)
      return
    }
    setLoading(true)
    if (mode === "login") {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) setError(error.message)
    } else {
      const { error } = await supabase.auth.signUp({ email, password })
      if (error) setError(error.message)
      else setSignupSuccess(true)
    }
    setLoading(false)
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm w-full max-w-sm p-8">
        <div className="text-center mb-8">
          <img src="/baulit-logo-dark.svg" alt="Baulit" className="h-14 mx-auto rounded-lg" />
        </div>

        {signupSuccess ? (
          <div className="text-center">
            <p className="text-green-600 font-semibold text-sm mb-2">✓ Account created!</p>
            <p className="text-slate-500 text-xs">Check your email to confirm your account, then log in.</p>
            <button onClick={() => { setMode("login"); setSignupSuccess(false); }}
              className="mt-4 text-amber-600 text-sm font-semibold hover:text-amber-700">← Back to login</button>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com" autoFocus tabIndex={1}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••" tabIndex={2}
                    onKeyDown={e => { if (e.key === "Enter") handleSubmit() }}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                  <button type="button" onClick={() => setShowPassword(s => !s)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    tabIndex={-1} aria-label={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-4.5 h-4.5">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-4.5 h-4.5">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            {error && <p className="text-red-500 text-xs">{error}</p>}
            {success && <p className="text-green-600 text-xs font-semibold">{success}</p>}
              {mode === "login" && (
                <div className="text-right">
                  <button type="button" onClick={async () => {
                    if (!email.trim()) { setError("Enter your email address first, then click Forgot Password."); return; }
                    setError(""); setSuccess(""); setLoading(true);
                    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: window.location.origin });
                    setLoading(false);
                    if (error) setError(error.message);
                    else setSuccess("Password reset link sent — check your email.");
                  }} className="text-xs text-amber-600 hover:text-amber-700 font-semibold">
                    Forgot Password?
                  </button>
                </div>
              )}
              {mode === "signup" && (
                <div>
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id="tos-agree"
                      checked={tosAgreed}
                      onChange={e => { setTosAgreed(e.target.checked); setTosError(false); }}
                      className="mt-0.5 rounded border-slate-300 accent-amber-500" />
                    <label htmlFor="tos-agree" className="text-xs text-slate-500 leading-relaxed">
                      I agree to the{" "}
                      <a href={TERMLY_TOS} target="_blank" rel="noopener noreferrer" className="text-amber-600 font-semibold hover:text-amber-700 underline">Terms of Service</a>
                      {" "}and{" "}
                      <a href={TERMLY_PRIVACY} target="_blank" rel="noopener noreferrer" className="text-amber-600 font-semibold hover:text-amber-700 underline">Privacy Policy</a>
                    </label>
                  </div>
                  {tosError && <p className="text-red-500 text-xs mt-1">You must agree to the Terms of Service and Privacy Policy.</p>}
                </div>
              )}
              <button onClick={handleSubmit} disabled={loading}
                className="w-full bg-amber-500 hover:bg-amber-400 text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-50">
                {loading ? "Please wait…" : mode === "login" ? "Log In" : "Create Account"}
              </button>
            </div>
            <div className="text-center mt-5">
              {mode === "login" ? (
                <p className="text-xs text-slate-400">Don't have an account?{" "}
                  <button onClick={() => { setMode("signup"); setTosAgreed(false); setTosError(false); }} className="text-amber-600 font-semibold hover:text-amber-700">Sign up</button>
                </p>
              ) : (
                <p className="text-xs text-slate-400">Already have an account?{" "}
                  <button onClick={() => setMode("login")} className="text-amber-600 font-semibold hover:text-amber-700">Log in</button>
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
